/** @forge-header
 * @id          totalParsers
 * @role        Total ticker parsers | all sports over/under market parsing
 * @pattern     pure-fn
 * @purity      none
 * @units       n/a
 * @upstream    kalshiTicker/types:ParsedTicker,GameInfo,TotalInfo,TickerCategory | kalshiTicker/helpers:labelToSport,parseFallback | nbaConsolidated/teamNames:teamNameFromCode
 * @downstream  kalshiTicker/parse
 * @exports     parseNbaTotal:fn | parseNflTotal:fn | parseNhlTotal:fn | parseNcaaMbTotal:fn | parseNcaaWbTotal:fn
 * @test        parse.test.ts (indirect)
 */

import type { ParsedTicker, GameInfo, TotalInfo, TickerCategory } from '../types';
import { parseDateFromTicker } from '../types';
import {
  labelToSport,
  parseFallback,
  mlbTeamFromCode,
  splitMlbDoubleheaderSuffix,
} from '../helpers';
import { teamNameFromCode, splitWnbaTeams, splitNflTeams, splitNhlTeams } from '../teamNames';

/** Splitter signature: take a packed team-pair chunk, return [away, home] or null. */
type TeamSplitter = (pair: string) => [string, string] | null;

/** Default splitter: fixed 3+3 slice (NBA, MLB, NCAA). */
function defaultSplit(pair: string): [string, string] | null {
  if (pair.length < 6) return null;
  return [pair.slice(0, 3), pair.slice(3, 6)];
}

/** NBA total (over/under): KXNBATOTAL-26FEB04BOSMIA-O */
export function parseNbaTotal(raw: string): ParsedTicker {
  return parseTotalGeneric(raw, 'KXNBATOTAL', 'nba-total', 'NBA');
}

/** WNBA total: KXWNBATOTAL-26MAY13LVCONN-160 (variable-length codes) */
export function parseWnbaTotal(raw: string): ParsedTicker {
  return parseTotalGeneric(raw, 'KXWNBATOTAL', 'wnba-total', 'WNBA', splitWnbaTeams);
}

/** NFL total: KXNFLTOTAL-26SEP09NESEA-24 (variable-length codes) */
export function parseNflTotal(raw: string): ParsedTicker {
  return parseTotalGeneric(raw, 'KXNFLTOTAL', 'nfl-total', 'NFL', splitNflTeams);
}

/** NHL total: KXNHLTOTAL-26FEB08SEATOR-O */
export function parseNhlTotal(raw: string): ParsedTicker {
  return parseTotalGeneric(raw, 'KXNHLTOTAL', 'nhl-total', 'NHL', splitNhlTeams);
}

/** NCAA Men's Basketball total */
export function parseNcaaMbTotal(raw: string): ParsedTicker {
  return parseTotalGeneric(raw, 'KXNCAAMBTOTAL', 'ncaa-total', 'NCAAMB');
}

/**
 * Soccer full-game goal total: KXWCTOTAL-26JUN14CIVECU-1 (also UECL /
 * INTLFRIENDLY). Outcome is the integer strike; the over/under line resolves
 * from market data (subtitle "Over 0.5 goals scored"). 3-char FIFA country
 * codes → default 3+3 split; series stem varies by competition.
 */
export function parseSoccerTotal(raw: string): ParsedTicker {
  const series = raw.split('-')[0] || 'KXWCTOTAL';
  return parseTotalGeneric(raw, series, 'soccer-total', 'SOCCER');
}

/** NCAA Women's Basketball total */
export function parseNcaaWbTotal(raw: string): ParsedTicker {
  return parseTotalGeneric(raw, 'KXNCAAWBTOTAL', 'ncaa-total', 'NCAAWB');
}

/**
 * MLB total: KXMLBTOTAL-26MAY12NYYBAL-9 or
 * KXMLBTOTAL-26MAY121610NYYBAL-9 (with optional HHMM).
 *
 * Outcome is a strike value (integer). Lines are floorStrike + 0.5 at the
 * consumer level; the parser just surfaces the labels. Disambiguates 2-vs-3
 * team-code lengths by trying the known MLB code registry.
 */
export function parseMlbTotal(raw: string): ParsedTicker {
  const parts = raw.split('-');
  if (parts.length < 3) return parseFallback(raw);

  const mid = parts[1] ?? '';
  if (mid.length < 7) return parseFallback(raw);

  const date = parseDateFromTicker(mid.slice(0, 7));
  if (!date) return parseFallback(raw);

  // Strip optional HHMM, then the doubleheader G1/G2 suffix
  // (26JUL221305PITNYYG1 — without this the 3+3 fallback split reads the
  // home team as "NYYG1").
  let teamBlob = mid.slice(7);
  if (/^\d{4}/.test(teamBlob)) teamBlob = teamBlob.slice(4);
  const dh = splitMlbDoubleheaderSuffix(teamBlob);
  teamBlob = dh.teams;

  // Try splitting at every viable boundary; accept the first split where both
  // halves resolve to a known MLB team. Falls back to even split (3+3) when
  // neither half is in the registry — fine for matchups against new codes.
  let awayCode: string | null = null;
  let homeCode: string | null = null;
  for (let i = 2; i <= 3; i++) {
    const a = teamBlob.slice(0, i);
    const b = teamBlob.slice(i);
    if (mlbTeamFromCode(a) && mlbTeamFromCode(b)) {
      awayCode = a;
      homeCode = b;
      break;
    }
  }
  if (!awayCode || !homeCode) {
    // Fallback: 3+3.
    if (teamBlob.length < 6) return parseFallback(raw);
    awayCode = teamBlob.slice(0, 3);
    homeCode = teamBlob.slice(3);
  }

  const game: GameInfo = {
    awayCode,
    homeCode,
    awayName: mlbTeamFromCode(awayCode) ?? undefined,
    homeName: mlbTeamFromCode(homeCode) ?? undefined,
    gameNumber: dh.gameNumber,
  };

  const outcome = parts[2] ?? '';
  const awayDisplay = game.awayName ?? awayCode;
  const homeDisplay = game.homeName ?? homeCode;
  const dhLabel = dh.gameNumber ? ` (G${dh.gameNumber})` : '';
  const shortLabel = `MLB Total: ${awayCode} @ ${homeCode}${dhLabel} O${outcome}`;
  const fullLabel = `MLB Total: ${awayDisplay} @ ${homeDisplay}${dhLabel} · Over ${outcome}`;

  const total: TotalInfo = {
    totalValue: 0,
    side: 'over',
  };

  return {
    raw,
    category: 'mlb-total',
    series: 'KXMLBTOTAL',
    date,
    game,
    player: null,
    spread: null,
    total,
    tennis: null,
    outcome,
    shortLabel,
    fullLabel,
  };
}

/** Generic total parser for all sports */
function parseTotalGeneric(
  raw: string,
  series: string,
  category: TickerCategory,
  label: string,
  splitter: TeamSplitter = defaultSplit
): ParsedTicker {
  const parts = raw.split('-');
  if (parts.length < 2) {
    return parseFallback(raw);
  }

  const mid = parts[1] ?? '';
  // WNBA codes can be as short as 2 chars → mid as short as 7+2+2 = 11.
  if (mid.length < 11) {
    return parseFallback(raw);
  }

  const date = parseDateFromTicker(mid.slice(0, 7));
  const teamCodes = mid.slice(7);

  const split = splitter(teamCodes);
  if (!split) {
    return parseFallback(raw);
  }

  const [awayCode, homeCode] = split;
  const outcome = parts[2]?.toUpperCase() ?? null;

  const game: GameInfo = { awayCode, homeCode };

  // Get team names for sports with lookup tables
  const sport = labelToSport(label);
  if (sport) {
    game.awayName = teamNameFromCode(awayCode, sport);
    game.homeName = teamNameFromCode(homeCode, sport);
  }

  const awayDisplay = game.awayName ?? awayCode;
  const homeDisplay = game.homeName ?? homeCode;

  const side = outcome === 'U' ? 'under' : 'over';
  const sideLabel = side === 'over' ? 'Over' : 'Under';

  const shortLabel = `${label} Total: ${awayCode} @ ${homeCode} ${sideLabel}`;
  const fullLabel = `${label} Total: ${awayDisplay} @ ${homeDisplay} · ${sideLabel}`;

  // Note: Actual total value comes from market data, not ticker
  const total: TotalInfo = {
    totalValue: 0, // Will be populated from market data
    side,
  };

  return {
    raw,
    category,
    series,
    date,
    game,
    player: null,
    spread: null,
    total,
    tennis: null,
    outcome,
    shortLabel,
    fullLabel,
  };
}
